import React, { type CSSProperties } from 'react';
import { StyleSheet, Text, View, type ViewStyle } from 'react-native';

import { APP_NAME } from '../../config/branding';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { APP_STORE_URL, PLAY_STORE_URL } from './landingCopy';

type StoreBadgesProps = {
  compact?: boolean;
};

const ANCHOR: CSSProperties = {
  display: 'inline-flex',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  borderRadius: radii.md,
};

export function StoreBadges(_props: StoreBadgesProps) {
  return (
    <View style={styles.row}>
      <style>{STORE_BADGE_CSS}</style>
      <a
        className="ml-store-badge"
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Get ${APP_NAME} on Google Play`}
        style={ANCHOR}
      >
        <View style={styles.badge}>
          <PlayMark />
          <View style={styles.copy}>
            <Text style={styles.eyebrow}>Get it on</Text>
            <Text style={styles.name}>Google Play</Text>
          </View>
        </View>
      </a>
      {APP_STORE_URL ? (
        <a
          className="ml-store-badge"
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Download ${APP_NAME} on the App Store`}
          style={ANCHOR}
        >
          <View style={styles.badge}>
            <AppleMark />
            <View style={styles.copy}>
              <Text style={styles.eyebrow}>Download on the</Text>
              <Text style={styles.name}>App Store</Text>
            </View>
          </View>
        </a>
      ) : (
        <View
          accessibilityRole="text"
          accessibilityLabel={`${APP_NAME} on the App Store — coming soon`}
          style={[styles.badge, styles.badgeSoon]}
        >
          <AppleMark muted />
          <View style={styles.copy}>
            <Text style={styles.eyebrow}>Coming soon</Text>
            <Text style={styles.name}>App Store</Text>
          </View>
        </View>
      )}
    </View>
  );
}

function PlayMark() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden style={{ display: 'block', flexShrink: 0 }}>
      <path fill="#00F076" d="M3.6 2.4 14.2 12 3.6 21.6V2.4z" />
      <path fill="#FFE14D" d="M16.7 9.5 14.2 12l2.5 2.5 4.1-2.4c.9-.5.9-1.7 0-2.2l-4.1-2.4z" />
      <path fill="#F85A40" d="M3.6 21.6 14.2 12l2.5 2.5-11.4 6.6c-.8.5-1.7-.1-1.7-1z" />
      <path fill="#00D0FF" d="M3.6 2.4c0-.9.9-1.5 1.7-1L16.7 8 14.2 12 3.6 2.4z" />
    </svg>
  );
}

function AppleMark({ muted }: { muted?: boolean }) {
  const fill = muted ? 'rgba(244,247,251,0.82)' : colors.text;
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden style={{ display: 'block', flexShrink: 0 }}>
      <path
        fill={fill}
        d="M16.7 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.2-2.8.9-3.5.9s-1.8-8-3.1-.9c-.8 0-2.2.6-2.9 1.6-1.3 2.2-.3 5.5 1 7.3.6.9 1.4 1.9 2.4 1.8 1-.04 1.3-.6 2.5-.6s1.5.6 2.5.6 1.7-.9 2.3-1.8c.8-1.1 1.1-2.1 1.1-2.2-.02-.01-2.1-.8-2.1-3.2zM14.8 5.8c.5-.6.9-1.5.8-2.4-.8 0-1.7.5-2.3 1.2-.5.6-.9 1.5-.8 2.3.9.1 1.8-.4 2.3-1.1z"
      />
    </svg>
  );
}

const STORE_BADGE_CSS = `
  a.ml-store-badge { outline: none; }
  a.ml-store-badge:hover > div { border-color: rgba(255,255,255,0.38); }
  a.ml-store-badge:focus-visible {
    outline: 2px solid ${colors.accent};
    outline-offset: 3px;
  }
  @media (prefers-reduced-motion: reduce) {
    a.ml-store-badge > div { transition: none !important; }
  }
`;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  badge: {
    minHeight: 52,
    minWidth: 176,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
    backgroundColor: '#0A0C10',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    transitionProperty: 'border-color',
    transitionDuration: '180ms',
    transitionTimingFunction: 'ease-out',
  } as ViewStyle,
  badgeSoon: {
    opacity: 0.72,
    cursor: 'default',
  } as ViewStyle,
  copy: {
    gap: 1,
  },
  eyebrow: {
    ...typography.label,
    fontSize: 10,
    letterSpacing: 0.4,
    color: 'rgba(244,247,251,0.72)',
    textTransform: 'uppercase',
  },
  name: {
    ...typography.headline,
    fontSize: 16,
    lineHeight: 20,
    color: colors.text,
  },
});
